import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, UserPlus, CalendarClock, CheckCircle, IndianRupee, XCircle, CreditCard, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const COLORS = ['#0f172a', '#d97706', '#10b981', '#6366f1', '#ef4444', '#06b6d4', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6'];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [paymentStats, setPaymentStats] = useState(null);
  const [filters, setFilters] = useState({});
  const [masters, setMasters] = useState({ courses: [], sources: [], users: [] });

  useEffect(() => {
    Promise.all([
      API.get('/masters/courses'),
      API.get('/masters/lead-sources'),
      API.get('/users')
    ]).then(([c, s, u]) => setMasters({ courses: c.data, sources: s.data, users: u.data })).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (filters.course) params.course = filters.course;
    if (filters.source) params.source = filters.source;
    if (filters.counsellor) params.counsellor = filters.counsellor;
    API.get('/dashboard/stats', { params }).then(r => setStats(r.data)).catch(() => {});
    API.get('/dashboard/charts', { params }).then(r => setCharts(r.data)).catch(() => {});
    API.get('/dashboard/payment-stats').then(r => setPaymentStats(r.data)).catch(() => {});
  }, [filters]);

  const kpis = stats ? [
    { label: 'Total Leads', value: stats.total_leads, icon: Users, color: 'text-slate-700', bg: 'bg-slate-50' },
    { label: 'Fresh This Week', value: stats.fresh_this_week, icon: UserPlus, color: 'text-blue-700', bg: 'bg-blue-50' },
    { label: 'Pending Follow Ups', value: stats.pending_follow_ups, icon: CalendarClock, color: 'text-amber-700', bg: 'bg-amber-50' },
    { label: 'Registration', value: stats.registration, icon: CheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-50' },
    { label: 'Fee Paid', value: stats.fee_paid, icon: IndianRupee, color: 'text-green-700', bg: 'bg-green-50' },
    { label: 'Lost', value: stats.lost, icon: XCircle, color: 'text-red-700', bg: 'bg-red-50' },
  ] : [];

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Admissions overview and analytics</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filters.course || "all"} onValueChange={v => setFilters(p => ({ ...p, course: v === 'all' ? undefined : v }))}>
            <SelectTrigger className="w-36 h-9 text-xs" data-testid="filter-course"><SelectValue placeholder="All Courses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Courses</SelectItem>
              {masters.courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.source || "all"} onValueChange={v => setFilters(p => ({ ...p, source: v === 'all' ? undefined : v }))}>
            <SelectTrigger className="w-36 h-9 text-xs" data-testid="filter-source"><SelectValue placeholder="All Sources" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {masters.sources.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((kpi, i) => (
          <div key={kpi.label} className={`kpi-card animate-fade-in animate-fade-in-delay-${i + 1}`} data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <div className={`p-2 rounded-lg ${kpi.bg}`}>
                <kpi.icon size={16} className={kpi.color} />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{kpi.value}</p>
            <p className="text-xs text-slate-500 mt-1 font-medium">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      {charts && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Leads by Course */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700">Leads by Course</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={charts.by_course}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#0f172a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Leads by Source */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700">Leads by Source</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={charts.by_source} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {charts.by_source.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2 justify-center">
                {charts.by_source.map((s, i) => (
                  <span key={s.name} className="flex items-center gap-1 text-xs text-slate-600">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    {s.name} ({s.count})
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Lead Trend */}
          <Card className="border-slate-200 shadow-sm lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700">Lead Trend (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={charts.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Line type="monotone" dataKey="count" stroke="#0f172a" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Leads by Stage */}
          <Card className="border-slate-200 shadow-sm lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700">Funnel - Leads by Stage</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={charts.by_stage} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} width={150} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#d97706" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payment Metrics */}
      {paymentStats && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Payment Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { label: 'Total Fees Planned', value: `₹${Number(paymentStats.total_fees_planned || 0).toLocaleString('en-IN')}`, icon: IndianRupee, color: 'text-slate-700', bg: 'bg-slate-50' },
              { label: 'Total Received', value: `₹${Number(paymentStats.total_fees_received || 0).toLocaleString('en-IN')}`, icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-50' },
              { label: 'Outstanding', value: `₹${Number(paymentStats.outstanding_fees || 0).toLocaleString('en-IN')}`, icon: CreditCard, color: 'text-amber-700', bg: 'bg-amber-50' },
              { label: 'Pending Payments', value: paymentStats.pending_payments_count || 0, icon: CalendarClock, color: 'text-blue-700', bg: 'bg-blue-50' },
              { label: 'Overdue', value: paymentStats.overdue_payments_count || 0, icon: AlertTriangle, color: 'text-red-700', bg: 'bg-red-50' },
            ].map((kpi, i) => (
              <div key={kpi.label} className="kpi-card" data-testid={`payment-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2 rounded-lg ${kpi.bg}`}><kpi.icon size={16} className={kpi.color} /></div>
                </div>
                <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{kpi.value}</p>
                <p className="text-xs text-slate-500 mt-1 font-medium">{kpi.label}</p>
              </div>
            ))}
          </div>
          {paymentStats.course_wise_collections?.length > 0 && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700">Course-wise Fee Collection</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={paymentStats.course_wise_collections}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={v => [`₹${Number(v).toLocaleString('en-IN')}`, 'Collected']} />
                    <Bar dataKey="amount" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
